import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MenuModule } from 'primeng/menu';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { RippleModule } from 'primeng/ripple';
import { MenuItem } from 'primeng/api';
import { TranslocoModule } from '@jsverse/transloco';
import { AuthService } from '@app/modules/auth/services';
import { ChatStore } from '@modules/chat/services/chat.store';
import { ChatSseService } from '@modules/chat/services/chat-sse.service';
import { NotificationStore } from '@modules/notifications/services/notification.store';
import { NotificationBellComponent } from '@modules/notifications/components/notification-bell/notification-bell.component';

const DEFAULT_PAGE_TITLE_KEY = 'layout.topbar.dashboardTitle';

@Component({
  standalone: true,
  selector: 'app-dashboard-topbar',
  templateUrl: './dashboard-topbar.component.html',
  styleUrl: './dashboard-topbar.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    MenuModule,
    AvatarModule,
    BadgeModule,
  RippleModule,
  TranslocoModule,
  NotificationBellComponent
  ]
})
export class DashboardTopbarComponent implements OnInit {
  @Input() isMobile: boolean = false;
  @Output() sidebarToggle = new EventEmitter<void>();

  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly chatStore = inject(ChatStore);
  private readonly chatSse = inject(ChatSseService);
  private readonly notificationStore = inject(NotificationStore);
  private readonly destroyRef = inject(DestroyRef);

  readonly unreadMessages = this.chatStore.unreadCount;
  readonly hasUnread = this.chatStore.hasUnread;

  /** Klucz tłumaczenia tytułu strony z data.titleKey aktualnej trasy */
  readonly pageTitleKey = signal(DEFAULT_PAGE_TITLE_KEY);

  constructor() {
    this.pageTitleKey.set(this.resolvePageTitleKey());
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.pageTitleKey.set(this.resolvePageTitleKey()));
  }

  ngOnInit(): void {
    this.chatStore.loadUnreadCount().subscribe();
    this.notificationStore.initOnce();

    this.chatSse.onUnreadCountChanged()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.chatStore.loadUnreadCount().subscribe());
  }

  /**
   * Schodzi po drzewie aktywnych tras i zwraca najgłębiej zdefiniowany
   * data.titleKey; fallback na domyślny tytuł pulpitu.
   */
  private resolvePageTitleKey(): string {
    let titleKey = DEFAULT_PAGE_TITLE_KEY;
    let route = this.router.routerState.snapshot.root;
    while (route) {
      const routeTitleKey = route.data?.['titleKey'] as string | undefined;
      if (routeTitleKey) {
        titleKey = routeTitleKey;
      }
      if (!route.firstChild) {
        break;
      }
      route = route.firstChild;
    }
    return titleKey;
  }

  currentDate = new Date();
  searchValue = '';

  userMenuItems: MenuItem[] = [
    {
      label: 'Profil',
      icon: 'pi pi-user',
      command: () => this.navigateToProfile()
    },
    {
      label: 'Ustawienia',
      icon: 'pi pi-cog',
      command: () => this.navigateToSettings()
    },
    {
      separator: true
    },
    {
      label: 'Wyloguj',
      icon: 'pi pi-sign-out',
      command: () => this.logout()
    }
  ];

  notificationCount = 3;
  userName = 'Jan Kowalski';

  onMenuToggle(): void {
    this.sidebarToggle.emit();
  }

  onSearch(): void {
    if (this.searchValue.trim()) {
      console.log('Searching for:', this.searchValue);
      // Implement search functionality
    }
  }

  onNotificationClick(): void {
    console.log('Opening notifications');
    // Implement notification panel
  }

  onChatClick(): void {
    this.router.navigate(['/chat']);
  }

  private navigateToProfile(): void {
    this.router.navigate(['/profile']);
  }

  private navigateToSettings(): void {
    this.router.navigate(['/settings']);
  }

  private logout(): void {
    this.authService.logout();
  }

  get formattedDate(): string {
    return this.currentDate.toLocaleDateString('pl-PL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }
}
